import React, { useState, useEffect } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { X, Settings, Save, RotateCcw } from 'lucide-react';

interface SettingsModalProps {
  onClose: () => void;
  onSave: (categories: string[], categoryMapping: Record<string, string>) => void;
  currentCategories?: string[];
}

const DEFAULT_CATEGORIES = ['What I am working on', 'What I am learning', 'What I am interested'];

export const SettingsModal: React.FC<SettingsModalProps> = ({ onClose, onSave, currentCategories }) => {
  const [isHovered, setIsHovered] = useState(false);
  // Use currentCategories if provided, otherwise load from localStorage or use defaults
  const getInitialCategories = () => {
    if (currentCategories && currentCategories.length > 0) {
      return currentCategories;
    }
    const savedCategories = localStorage.getItem('cleanRoomCategories');
    if (savedCategories) {
      try {
        const parsed = JSON.parse(savedCategories);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch (e) {
        console.error('Failed to parse saved categories');
      }
    }
    return DEFAULT_CATEGORIES;
  };

  const initialCats = getInitialCategories();
  const [categories, setCategories] = useState<string[]>(initialCats);
  const [originalCategories] = useState<string[]>(initialCats);

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleCategoryChange = (index: number, value: string) => {
    const newCategories = [...categories];
    newCategories[index] = value;
    setCategories(newCategories);
  };

  const handleAddCategory = () => {
    if (categories.length < 10) {
      setCategories([...categories, `Category ${categories.length + 1}`]);
    }
  };

  const handleRemoveCategory = (index: number) => {
    if (categories.length > 1) {
      const categoryToRemove = categories[index];
      // Warn if there might be items using this category
      if (window.confirm(`Are you sure you want to remove "${categoryToRemove}"? Items using this category will need to be reassigned.`)) {
        const newCategories = categories.filter((_, i) => i !== index);
        setCategories(newCategories);
      }
    } else {
      alert('You must have at least one category.');
    }
  };

  const handleSave = () => {
    // Filter out empty categories
    const validCategories = categories.filter(cat => cat.trim() !== '');
    
    if (validCategories.length === 0) {
      alert('Please provide at least one category');
      return;
    }
    
    // Create a mapping from old to new categories
    const categoryMapping: Record<string, string> = {};
    for (let i = 0; i < originalCategories.length && i < validCategories.length; i++) {
      if (originalCategories[i] !== validCategories[i]) {
        categoryMapping[originalCategories[i]] = validCategories[i];
      }
    }
    
    // Save to localStorage
    localStorage.setItem('cleanRoomCategories', JSON.stringify(validCategories));
    onSave(validCategories, categoryMapping);
    onClose();
  };

  const handleReset = () => {
    setCategories(DEFAULT_CATEGORIES);
  };

  return (
    <div style={{...styles.position.fixed, zIndex: 50 }} onClick={onClose}>
      <div style={{...styles.spacing.py4, 
    ...styles.size.minHScreen,
    ...styles.spacing.px4,
    ...styles.layout.flex,
     alignItems: "center" ,
     justifyContent: "center" 
  }}>
        <div style={{
    ...styles.background.white,
     borderRadius: "var(--radius-2xl)" ,
     maxWidth: "28rem" ,
    ...styles.size.wFull,
    ...styles.spacing.paddingXl
  }} onClick={(e) => e.stopPropagation()}>
        <div style={{
    ...styles.layout.flex,
     justifyContent: "space-between" ,
     alignItems: "center" ,
    ...styles.spacing.marginBottomXl
  }}>
          <div style={{
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap3
  }}>
            <div style={{...styles.extended.gradientBluePurple, 
    ...styles.spacing.paddingSm,
    ...styles.utility.roundedLg
  }}>
              <Settings style={{...styles.typography.textWhite}} size={20} />
            </div>
            <div>
              <h2 style={{
    ...styles.typography.textXl,
    ...styles.typography.fontBold,
     color: "var(--color-gray-900)" 
  }}>Manage Categories</h2>
              <p style={{
    ...styles.typography.textXs,
     color: "var(--color-gray-500)" ,
    ...styles.spacing.marginTopXs
  }}>Add, rename, or remove your Level 1 categories</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
    ...styles.spacing.paddingSm ,
    ...styles.utility.roundedLg,
  }}
          >
            <X size={20} style={{
     color: "var(--color-gray-600)" 
  }} />
          </button>
        </div>

        <div style={{
     gap: "var(--space-3)" ,
    ...styles.spacing.marginBottomXl
  }}>
          <p style={{
    ...styles.typography.textSm,
     color: "var(--color-gray-600)" ,
    ...styles.spacing.marginBottomLg
  }}>
            Customize your Level 1 category names to match your workflow
          </p>
          
          {categories.map((category, index) => (
            <div key={index} style={{
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap2
  }}>
              <span style={{
    ...styles.typography.textSm,
    ...styles.typography.fontMedium,
     color: "var(--color-gray-500)" ,
    ...styles.size.w8
  }}>
                {index + 1}.
              </span>
              <input
                type="text"
                value={category}
                onChange={(e) => handleCategoryChange(index, e.target.value)}
                style={{...styles.utility.border, 
     flex: 1 ,
    ...styles.spacing.px3,
    ...styles.spacing.py2,
    ...styles.utility.border,
    ...styles.utility.roundedLg
  }}
                placeholder={`Category ${index + 1}`}
              />
              {categories.length > 1 && (
                <button
                  onClick={() => handleRemoveCategory(index)}
                  onMouseEnter={() => setIsHovered(true)} 
                  onMouseLeave={() => setIsHovered(false)}
                  style={{
                    ...(isHovered ? styles.states.hover.gray50 : {}),
    ...styles.spacing.paddingSm,
     color: "#dc2626" ,
    ...styles.utility.roundedLg,
  }}
                  title="Remove category"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        </div>

        <div style={{
    ...styles.layout.flex,
    ...styles.spacing.gap2,
    ...styles.spacing.marginBottomXl
  }}>
          {categories.length < 10 && (
            <button
              onClick={handleAddCategory}
              style={{...styles.background.gray50, 
     flex: 1 ,
    ...styles.spacing.px4,
    ...styles.spacing.py2,
     color: "#2563eb",
    ...styles.utility.roundedLg,
    ...styles.typography.textSm,
    ...styles.typography.fontMedium
  }}
            >
              + Add Category
            </button>
          )}
          <button
            onClick={handleReset}
            style={{
    ...styles.spacing.px4,
    ...styles.spacing.py2,
     color: "var(--color-gray-600)" ,
    ...styles.background.gray100,
    ...styles.utility.roundedLg ,
    ...styles.typography.textSm,
    ...styles.typography.fontMedium,
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap2
  }}
          >
            <RotateCcw size={14} />
            Reset to Default
          </button>
        </div>

        <div style={{...styles.extended.pt4, 
    ...styles.layout.flex,
     justifyContent: "flex-end" ,
    ...styles.spacing.gap3,
    ...styles.utility.borderTop
  }}>
          <button
            onClick={onClose}
            style={{
     color: "var(--color-gray-700)" ,
    ...styles.background.gray100,
    ...styles.utility.roundedLg ,
    ...styles.typography.fontMedium
  }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{...(isHovered ? styles.states.hover.gray50 : {...styles.extended.px5, ...styles.extended.py25}),
    ...styles.typography.textWhite,
    ...styles.utility.roundedLg,
    ...styles.typography.fontMedium,
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap2
  }}
          >
            <Save size={16} />
            Save Changes
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};